/**
 * EL ÚNICO `AudioContext` DE LA CASA. Uno, y todo lo que suena cuelga de él.
 *
 * DE DÓNDE SALE, Y ES UN CHROMEBOOK DE INSTITUTO (25 de septiembre de 2026). Gabri: al entrar
 * al mundo 3D, «da un pitido intenso y no se escucha la música; después del pip ya no suena nada
 * más». En su Mac no pasa. Al ir a mirarlo aparecieron **cinco contextos de audio distintos**
 * —`sound`, `ambient`, `runnerBeat`, `fireSound` y los efectos del Recorrido—, cada uno abierto
 * por su cuenta el día que hizo falta, y ninguno sabiendo de los otros.
 *
 * POR QUÉ ESO IMPORTA, Y NO ES ELEGANCIA:
 *
 *   · **Cada contexto es un hilo de audio de verdad.** Cinco hilos pidiendo su trozo de sonido
 *     cada pocos milisegundos, en una máquina que además está dibujando un mundo 3D, es
 *     exactamente la receta del corte: cuando uno no llega a tiempo, la tarjeta repite el
 *     último trozo, y eso se oye como **un pitido que se queda**.
 *   · **Y hay un techo.** Chrome no deja abrir contextos sin límite en una misma página: pasado
 *     el tope, `new AudioContext()` **lanza**, y a partir de ahí no suena nada más. Con cinco
 *     abiertos y cualquiera que se sume, ese tope está a un paso —y el síntoma sería justo el
 *     que se contó: primero un ruido, después silencio para todo—.
 *
 * LO QUE **NO** CAMBIA: cada pieza sigue montando su propia cadena de nodos y conectándola a
 * `destination`. Aquí no se mezcla nada ni se manda sobre el volumen de nadie; lo único que se
 * comparte es el contexto, que es el recurso caro.
 *
 * PEREZOSO A PROPÓSITO: no se abre hasta que algo va a sonar. Un contexto creado antes de que
 * el alumno haya tocado la pantalla nace suspendido —la política de autoplay— y aun así cuenta
 * para el tope. Quien lo pida y lo encuentre suspendido, lo despierta aquí.
 *
 * Se prueba con `scripts/prueba_audio.mjs`, que además vigila que no vuelva a abrirse uno por
 * ahí suelto: es un fallo que no da error, sólo se oye —y sólo en las máquinas justas—.
 */

let ctx = null;
/** Si la pestaña no se ve. Arriba del todo porque `contexto` la usa (y `vigila`, más abajo). */
const escondida = (ventana) => !!(ventana && ventana.document && ventana.document.hidden);
/**
 * SI EL CONTEXTO ESTÁ PARADO, Y NO SÓLO «SUSPENDIDO». Safari tiene un tercer estado, `interrupted`
 * —una llamada, otra app que se queda el audio, la pantalla que se apaga—, y del que sólo se
 * sale con `resume()` igual que del otro. Preguntando por `suspended` a secas, un iPhone que
 * volvía de una interrupción se quedaba mudo para siempre (Gabri, 11 oct 2026: sin música ni en
 * Safari ni en el acceso directo).
 */
export const parado = (c) => !!c && c.state !== "running" && c.state !== "closed";

/**
 * El contexto de la casa. `null` si el navegador no tiene Web Audio o si se niega a abrirlo.
 *
 * @param {object} [ventana]  para poder probarlo con una ventana de mentira.
 */
export function contexto(ventana = typeof window === "undefined" ? null : window) {
  if (ctx) {
    // Y NO SE DESPIERTA CON LA PESTAÑA ESCONDIDA: eso lo ha callado `vigila`, y un sonido que se
    // pide en segundo plano no es motivo para volver a sonar encima de otra app.
    if (parado(ctx) && ctx.resume && !escondida(ventana)) { try { ctx.resume(); } catch { /* ya vendrá */ } }
    return ctx;
  }
  const AC = ventana && (ventana.AudioContext || ventana.webkitAudioContext);
  if (!AC) return null;
  suenaEnSilencio(ventana);
  try {
    ctx = new AC();
  } catch {
    // SE DEVUELVE `null` Y NO SE REVIENTA. Si el navegador no lo deja —tope de contextos,
    // permisos, una pestaña en segundo plano—, lo que tiene que pasar es que no suene, no que
    // se caiga la pantalla que lo pedía.
    ctx = null;
  }
  if (ctx) vigila(ventana);
  return ctx;
}

/**
 * EL SONIDO SE CALLA CUANDO NO SE VE, Y EN EL IPHONE SE DESPIERTA CON EL DEDO (roadmap móvil,
 * 10 de octubre de 2026).
 *
 *   · **Con la pestaña escondida, seguía sonando.** Cambiar de app en el móvil con la música del
 *     mundo puesta la dejaba sonando por encima de lo que fuera. Al esconderse se suspende el
 *     contexto —que es pararlo todo a la vez sin tocar ninguna pieza— y al volver se reanuda,
 *     **sólo si lo había parado esto**: si estaba suspendido por otra razón, no le toca a esto
 *     despertarlo.
 *   · **En el iPhone, Safari sólo deja arrancar el audio dentro de un toque**, y el que cuenta es
 *     `touchend`. Un contexto que nacía suspendido se quedaba así hasta que algo que sonara lo
 *     intentaba despertar fuera del gesto. Ahora lo despierta el primer toque que llegue.
 */
/**
 * QUE SUENE AUNQUE EL IPHONE ESTÉ EN SILENCIO. Gabri, 11 de octubre de 2026: *«era el modo
 * silencio: la música se escucha ahora por Safari y por el acceso directo. Quiero que suene
 * también pese al modo silencio»*.
 *
 * EN EL IPHONE, EL AUDIO DE UNA WEB ES «AMBIENTE» POR DEFECTO, y el ambiente lo calla el
 * interruptor —o el botón de acción— de silencio. `navigator.audioSession.type = "playback"`
 * (Safari 16.4) lo pasa a «reproducción», que es lo que usa un juego o un vídeo: suena con el
 * silencio puesto y lo sigue mandando el volumen. Lo que trae, y Gabri lo sabe: si el alumno
 * tenía música puesta en otra app, se pausa cuando empieza a sonar la Academia, como pasa con
 * cualquier vídeo.
 *
 * SÓLO SE PIDE DONDE EXISTE (Safari) y antes de abrir el contexto; en los demás navegadores el
 * modo silencio no calla la web, así que no hay nada que pedir. Y SE PIDE CON LA PESTAÑA A LA
 * VISTA: con la pestaña escondida el contexto se suspende (`vigila`), así que «reproducción» no
 * significa sonar en segundo plano.
 */
function suenaEnSilencio(ventana) {
  try {
    const sesion = ventana && ventana.navigator && ventana.navigator.audioSession;
    if (sesion && sesion.type !== "playback") sesion.type = "playback";
  } catch { /* un Safari que no lo deja: sonará como siempre, con el silencio mandando */ }
}

let vigilando = false;
function vigila(ventana) {
  if (vigilando || !ventana || !ventana.addEventListener) return;
  vigilando = true;
  const doc = ventana.document;
  let loParoYo = false;
  if (doc && doc.addEventListener) {
    doc.addEventListener("visibilitychange", () => {
      if (!ctx) return;
      try {
        if (doc.hidden && ctx.state === "running" && ctx.suspend) { loParoYo = true; ctx.suspend(); }
        else if (!doc.hidden && loParoYo && ctx.resume) { loParoYo = false; ctx.resume(); }
      } catch { /* a la próxima */ }
    });
  }
  const despierta = () => {
    try { if (parado(ctx) && !escondida(ventana) && ctx.resume) ctx.resume(); } catch { /* ya vendrá */ }
    if (ctx && ctx.state === "running") {
      ventana.removeEventListener("touchend", despierta);
      ventana.removeEventListener("pointerup", despierta);
    }
  };
  ventana.addEventListener("touchend", despierta, { passive: true });
  ventana.addEventListener("pointerup", despierta, { passive: true });
}

/** Si ya está abierto. Lo pregunta la tarjeta de Ajustes, que no quiere abrirlo para mirarlo. */
export function hay() {
  return !!ctx;
}

/** Cómo está, para enseñarlo. `null` si todavía no ha sonado nada. */
export function estado() {
  return ctx ? { estado: ctx.state, hz: ctx.sampleRate } : null;
}

/** Sólo para las pruebas: olvida el que hubiera, para poder empezar de cero. */
export function olvida() {
  ctx = null;
  vigilando = false;
}

export default { contexto, estado, hay, olvida };
